<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import { computedAsync } from '@vueuse/core';

import { isExperimentAsync } from '@libs/feature-toggle';

import SharedHeader from '../../core/components/SharedHeader.vue';
import { usePerformanceStore, useStatsigEventsStore } from '../../core/stores';
import {
  PriceSummaryFooterMobile,
  PriceSummaryFooterWithSaveTripMobile,
} from '../../modules/priceSummaryFooterMobile';

import TripSummaryColumn from './components/tripSummary/TripSummaryColumn.vue';
import TripOverviewPageLayoutMobile from './TripOverviewPageLayoutMobile.vue';
import TripOverviewColumn from './components/tripItinerary/TripOverviewColumn.vue';
import { TripOverviewPanelType } from './models';
import {
  useTripOverviewPageStore,
  useTripOverviewPanelsStore,
  useTripSummaryStore,
} from './stores';

const performanceStore = usePerformanceStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const tripSummaryStore = useTripSummaryStore();
const statsigEventsStore = useStatsigEventsStore();

const tripSummaryColumnRef = useTemplateRef('tripSummaryColumnRef');

const moblieFooterHeight = '84px';

const shouldShowSummaryMobileFooter = computed(
  () =>
    tripOverviewPageStore.hasRecipeIngredients &&
    !tripSummaryStore.isTripSummaryVisible &&
    tripOverviewPanelsStore.tripOverviewPanelType === TripOverviewPanelType.None
);

const shouldShowTripSummaryPreCheckout = computedAsync<boolean>(
  () => isExperimentAsync('trip_summary_pre_checkout_experiment'),
  false
);

const shouldShowSaveTripInMobileFooterExperiment = computedAsync<boolean>(
  () => isExperimentAsync('save_trip_in_mobile_footer'),
  false
);

function scrollToTripSummary(): void {
  statsigEventsStore.logTripOverviewScrollToSummary();

  if (shouldShowTripSummaryPreCheckout.value) {
    tripOverviewPanelsStore.openTripPriceSummaryPreCheckoutPanel();
  } else {
    const el = tripSummaryColumnRef.value?.$el;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}
</script>

<template>
  <TripOverviewPageLayoutMobile
    :class="{
      'has-mobile-footer':
        shouldShowSummaryMobileFooter &&
        !shouldShowSaveTripInMobileFooterExperiment,
    }"
    @vue:mounted="performanceStore.trackTripOverviewPageRenderedPerformance()"
  >
    <template #content>
      <SharedHeader :should-show-print-button="false" />
      <TripOverviewColumn />
    </template>
    <template #summary>
      <TripSummaryColumn ref="tripSummaryColumnRef" />
    </template>
  </TripOverviewPageLayoutMobile>

  <PriceSummaryFooterWithSaveTripMobile
    v-if="shouldShowSaveTripInMobileFooterExperiment"
    v-show="shouldShowSummaryMobileFooter"
    :button-state="tripSummaryStore.bookingButtonState"
    :button-text="$t('taxes_and_fees.summary')"
    class="trip-summary-mobile-footer-with-save-trip"
    @button-clicked="scrollToTripSummary"
  />

  <PriceSummaryFooterMobile
    v-else
    v-show="shouldShowSummaryMobileFooter"
    :button-state="tripSummaryStore.bookingButtonState"
    :button-text="$t('taxes_and_fees.summary')"
    class="trip-summary-mobile-footer"
    @button-clicked="scrollToTripSummary"
  />
</template>

<style lang="scss" scoped>
.has-mobile-footer {
  margin-bottom: v-bind(moblieFooterHeight);
}

.trip-summary-mobile-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  height: v-bind(moblieFooterHeight);
}

.trip-summary-mobile-footer-with-save-trip {
  position: fixed;
  bottom: 0;
  left: 0;
}
</style>
